//Node Modules
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";

//GraphQL

//Pages

//BinaryForge Components
import { PageHeader } from "../../../components/layout";
import { Note } from "../../../components/helpers";
import UserForm from "./UserForm";

//3rd Party Components
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";

//Atoms
import { toastAtom } from "../../../atoms/toast";

//Helpers
import { nav } from "../../../config/navigation";
import { useAddUserToScheme } from "../../../helpers/User";

const UserCreate = () => {
	//Hooks
	const { t } = useTranslation();
	const formRef = useRef();
	const navigate = useNavigate();
	const { isAdmin } = useLoaderData();
	const addToScheme = useAddUserToScheme();

	//Recoil State
	const setToast = useSetRecoilState(toastAtom);

	const getFailureDetail = (result, email) => {
		switch (result.status) {
			case "rolledBack":
				return t("user.addExisting.toast.rollbackDetail", { error: result.error });
			case "rollbackFailed":
				return t("user.addExisting.toast.rollbackFailedDetail", { email: email, error: result.error });
			case "notFound":
				return t("user.addExisting.notFound");
			default:
				return result.messageKey
					? t("user.addExisting.toast.errorDetail", { error: result.messageKey })
					: t("user.addExisting.toast.unknownDetail");
		}
	};

	const addUserToScheme = async (email, role) => {
		const result = await addToScheme(email, role, t("user.create.dupeUser.loading"));

		if (result.status === "added") {
			setToast({
				severity: "success",
				summary: t("user.addExisting.toast.successSummary"),
				detail: t("user.addExisting.toast.successDetail", { email: email }),
			});
			navigate(`${nav.user.base}/${nav.user.management}`);
			return;
		}

		setToast({
			severity: "error",
			summary:
				result.status === "rolledBack" || result.status === "rollbackFailed"
					? t("user.addExisting.toast.rollbackSummary")
					: t("user.addExisting.toast.errorSummary"),
			detail: getFailureDetail(result, email),
		});
	};

	const confirmAddUser = (email, role) => {
		confirmDialog({
			header: t("user.create.dupeUser.header"),
			message: (
				<>
					<Note
						messageKey={t("user.create.dupeUser.message", { email: email })}
						icon="pi pi-info-circle fontColour-info"
					/>
					<Note
						wrapperStyle="marginTop-small"
						messageKey={t("user.create.dupeUser.warning")}
						messageStyle="fontSize-small"
						icon="pi pi-exclamation-triangle fontColour-warn"
					/>
				</>
			),
			acceptLabel: t("user.create.dupeUser.accept"),
			rejectLabel: t("user.create.dupeUser.reject"),
			acceptIcon: "pi pi-user-plus",
			acceptClassName: "success feature",
			rejectClassName: "error",
			accept: () => addUserToScheme(email, role),
			reject: () => {}, //Do Nothing,
		});
	};

	return (
		<>
			<PageHeader title={isAdmin ? t("admin.user.create.pageTitle") : t("user.create.pageTitle")} enableBack />
			<main className="appMain">
				<div className="appMain-content">
					<div className="flex jContent-end gap-small">
						<Button
							className="feature"
							label={isAdmin ? t("admin.user.create.actions.submit") : t("user.create.actions.submit")}
							icon="pi pi-save"
							onClick={() => formRef.current.submitUserForm()}
						/>
					</div>
					<UserForm type="create" ref={formRef} dupeUser={confirmAddUser} />
				</div>
			</main>
		</>
	);
};

export default UserCreate;
